import { Check, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Shared pieces for the teammate pickers (AssignButton, WatcherPicker,
// AssigneePicker).
//
// The three components were near-duplicates in their parts but genuinely
// different in behaviour — AssignButton is single-select with a pinned "claim
// it myself" row, WatcherPicker is multi-select, AssigneePicker is a keyboard
// combobox. Collapsing them into one component would mean a prop surface with
// three mutually-exclusive modes, which is worse than the duplication.
//
// What WAS pure duplication: a byte-identical `CheckMark`, the same search
// input, and the same "No teammates match." empty state, copied between files.
// Those live here.

/** Search field used at the top of every teammate list. */
export function PickerSearch({
  value,
  onChange,
  placeholder = 'Search teammates…',
  autoFocus,
  id,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  id?: string;
}) {
  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        id={id}
        autoFocus={autoFocus}
        type="search"
        // The placeholder was previously the only label. It disappears the
        // moment you type, leaving the field with no accessible name.
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 pl-7 text-xs"
      />
    </div>
  );
}

/**
 * Selection indicator for a picker row.
 *
 * `aria-hidden` on purpose: the CHECKED STATE must be announced by the row
 * itself, via `role="menuitemcheckbox"` + `aria-checked` (single-select rows
 * use `role="menuitemradio"`). Previously this square was the only signal that
 * a teammate was selected and it was hidden from assistive tech, so a screen
 * reader user toggling watchers got no feedback at all about what was on.
 */
export function PickerCheck({ on }: { on: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border',
        on ? 'border-transparent bg-foreground text-background' : 'border-border bg-background',
      )}
      aria-hidden
    >
      {on ? <Check className="h-3 w-3" /> : null}
    </span>
  );
}

/** Empty state for a filtered teammate list. */
export function PickerEmpty({ searching }: { searching: boolean }) {
  return (
    <p className="px-3 py-3 text-xs text-muted-foreground">
      {searching ? 'No teammates match that search.' : 'No other teammates.'}
    </p>
  );
}

/** Shared row chrome. Callers supply the role/aria-checked appropriate to
 *  their selection model — see the note on `PickerCheck`. */
export const PICKER_ROW_CLASS =
  'flex w-full items-center gap-2.5 px-2.5 py-2 text-left text-xs hover:bg-accent/60 focus-visible:outline-none focus-visible:bg-accent focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50';
